import type { ReactNode } from 'react'

import { AgentImageStrip } from './AgentImageStrip'
import type { AgentTimelineImage } from '../../lib/agentTimelineMapper'

/** 轻量 Markdown：标题、粗体、斜体、行内代码、代码块、列表、表格、图片、分隔线、换行；不引入额外依赖 */
export function AgentMarkdown({ text }: { text: string }) {
  const blocks = splitBlocks(text)
  return (
    <div className="agent-md space-y-2 text-sm leading-7 text-ink">
      {blocks.map((block, index) => (
        <Block key={`${block.type}-${index}`} block={block} />
      ))}
    </div>
  )
}

type TableAlign = 'left' | 'center' | 'right'

type MdBlock =
  | { type: 'code'; lang: string; content: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'task'; items: { text: string; checked: boolean }[] }
  | { type: 'quote'; content: string }
  | { type: 'h'; level: 1 | 2 | 3; content: string }
  | { type: 'table'; header: string[]; aligns: TableAlign[]; rows: string[][] }
  | { type: 'images'; images: AgentTimelineImage[] }
  | { type: 'hr' }
  | { type: 'p'; content: string }

function splitBlocks(raw: string): MdBlock[] {
  const lines = raw.replace(/\r\n/g, '\n').split('\n')
  const out: MdBlock[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i] ?? ''
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim()
      const body: string[] = []
      i += 1
      while (i < lines.length && !(lines[i] ?? '').startsWith('```')) {
        body.push(lines[i] ?? '')
        i += 1
      }
      i += 1
      out.push({ type: 'code', lang, content: body.join('\n') })
      continue
    }
    if (isTableStart(lines, i)) {
      const header = splitRow(line)
      const aligns = splitRow(lines[i + 1] ?? '').map(cellAlign)
      const rows: string[][] = []
      i += 2
      while (i < lines.length && (lines[i] ?? '').trim() && (lines[i] ?? '').includes('|')) {
        rows.push(splitRow(lines[i] ?? ''))
        i += 1
      }
      out.push({ type: 'table', header, aligns, rows })
      continue
    }
    if (/^ {0,3}(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push({ type: 'hr' })
      i += 1
      continue
    }
    // 独立成行的 `![alt](url)`：渲染为图片条（与 display_images 工具同一组件，支持灯箱），不再当文本
    if (MD_IMAGE_RE.test(line)) {
      const images: AgentTimelineImage[] = []
      while (i < lines.length && MD_IMAGE_RE.test(lines[i] ?? '')) {
        const match = (lines[i] ?? '').match(MD_IMAGE_RE)
        if (match?.[2]) {
          images.push({ src: match[2], alt: match[1]?.trim() || match[3]?.trim() || undefined })
        }
        i += 1
      }
      out.push({ type: 'images', images })
      continue
    }
    // 中文语境下模型常输出 `##标题`（# 后无空格），同样按标题处理
    if (/^#{1,3}\s*\S/.test(line)) {
      const level = (line.match(/^#+/)?.[0].length ?? 1) as 1 | 2 | 3
      out.push({ type: 'h', level: Math.min(level, 3) as 1 | 2 | 3, content: line.replace(/^#{1,3}\s*/, '') })
      i += 1
      continue
    }
    // GFM 任务列表：`- [x]` / `- [ ]`（含 *、+ 列表符）
    if (TASK_ITEM_RE.test(line)) {
      const items: { text: string; checked: boolean }[] = []
      while (i < lines.length && TASK_ITEM_RE.test(lines[i] ?? '')) {
        const raw = lines[i] ?? ''
        const mark = raw.match(/^\s*[-*+]\s+\[([ xX])\]/)
        items.push({
          text: raw.replace(/^\s*[-*+]\s+\[[ xX]\]\s+/, ''),
          checked: mark?.[1]?.toLowerCase() === 'x',
        })
        i += 1
      }
      out.push({ type: 'task', items })
      continue
    }
    if (/^ {0,3}>/.test(line)) {
      const content: string[] = []
      while (i < lines.length && /^ {0,3}>/.test(lines[i] ?? '')) {
        content.push((lines[i] ?? '').replace(/^ {0,3}>\s?/, ''))
        i += 1
      }
      out.push({ type: 'quote', content: content.join('\n') })
      continue
    }
    if (isListItem(line)) {
      const items: string[] = []
      while (i < lines.length && isListItem(lines[i] ?? '')) {
        items.push(stripListMarker(lines[i] ?? ''))
        i += 1
      }
      out.push({ type: 'ul', items })
      continue
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\s*\d+\.\s+/, ''))
        i += 1
      }
      out.push({ type: 'ol', items })
      continue
    }
    if (!line.trim()) {
      i += 1
      continue
    }
    const para: string[] = [line]
    i += 1
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() &&
      !/^(```|#{1,3}\s*\S|\s*[-*+]\s+|\s*\d+\.\s+| {0,3}(-{3,}|\*{3,}|_{3,})\s*$| {0,3}>)/.test(lines[i] ?? '') &&
      !DASH_ITEM_RE.test(lines[i] ?? '') &&
      !MD_IMAGE_RE.test(lines[i] ?? '') &&
      !isTableStart(lines, i)
    ) {
      para.push(lines[i] ?? '')
      i += 1
    }
    out.push({ type: 'p', content: para.join('\n') })
  }
  return out
}

/** GFM 任务列表行：`- [x] 已完成` / `- [ ] 待办`（兼容 *、+ 列表符） */
const TASK_ITEM_RE = /^\s*[-*+]\s+\[[ xX]\]\s+/

/**
 * 独立成行的 Markdown 图片：`![alt](url)`，alt 可为空，标题可省略。
 * 仅整行匹配才按图片渲染；行内混排的图片语法不处理（模型展示图片走 display_images 工具）。
 */
const MD_IMAGE_RE = /^\s*!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)\s*$/

/**
 * 无空格短横列表行：中文语境模型常输出 `-提示词最专业…`（- 后无空格），
 * 标准列表正则不识别导致原样漏出。排除 `--`（分隔线前缀）、`—`（破折号）；
 * 只对 `-` 放宽，`*`/`+` 不放宽（`**bold**` 行首会被误切）。
 */
const DASH_ITEM_RE = /^\s*-(?![\s—–|])/

function stripListMarker(line: string): string {
  return line.replace(/^\s*[-*+]\s+/, '').replace(/^\s*-/, '')
}

function isListItem(line: string): boolean {
  if (TASK_ITEM_RE.test(line)) return false
  return /^\s*[-*+]\s+/.test(line) || DASH_ITEM_RE.test(line)
}

/** GFM 表格：标题行 + 分隔行（`|---|:--:|`）才算，避免把普通含竖线的文本误判成表格 */
function isTableStart(lines: string[], index: number): boolean {
  const header = lines[index] ?? ''
  const separator = lines[index + 1] ?? ''
  if (!header.includes('|') || !separator.includes('|') || !separator.includes('-')) return false
  const cells = splitRow(separator)
  return cells.length > 0 && cells.every((cell) => /^:?-{1,}:?$/.test(cell.replace(/\s+/g, '')))
}

function splitRow(line: string): string[] {
  let text = line.trim()
  if (text.startsWith('|')) text = text.slice(1)
  if (text.endsWith('|')) text = text.slice(0, -1)
  return text.split('|').map((cell) => cell.trim())
}

function cellAlign(separatorCell: string): TableAlign {
  const cell = separatorCell.replace(/\s+/g, '')
  const left = cell.startsWith(':')
  const right = cell.endsWith(':')
  if (left && right) return 'center'
  if (right) return 'right'
  return 'left'
}

function Block({ block }: { block: MdBlock }) {
  switch (block.type) {
    case 'code':
      return (
        <pre className="max-h-56 overflow-auto rounded-xl bg-[color-mix(in_srgb,var(--surface-muted)_70%,var(--panel)_30%)] px-3 py-3 text-xs leading-6 text-ink-soft">
          {block.content}
        </pre>
      )
    case 'ul':
      return (
        <ul className="list-disc space-y-1 pl-5">
          {block.items.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ul>
      )
    case 'ol':
      return (
        <ol className="list-decimal space-y-1 pl-5">
          {block.items.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ol>
      )
    case 'task':
      return (
        <ul className="space-y-1">
          {block.items.map((item, index) => (
            <li key={index} className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={item.checked}
                readOnly
                tabIndex={-1}
                aria-label={item.checked ? '已完成' : '未完成'}
                className="mt-[7px] h-3.5 w-3.5 shrink-0 cursor-default accent-[var(--accent)]"
              />
              <span className={item.checked ? 'text-ink-soft' : undefined}>{inline(item.text)}</span>
            </li>
          ))}
        </ul>
      )
    case 'quote':
      return (
        <blockquote className="border-l-2 border-[color-mix(in_srgb,var(--accent)_45%,transparent)] pl-3 text-ink-soft">
          <p className="whitespace-pre-wrap">{inline(block.content)}</p>
        </blockquote>
      )
    case 'h': {
      const className =
        block.level === 1 ? 'text-base font-semibold' : block.level === 2 ? 'text-sm font-semibold' : 'text-sm font-medium'
      return <p className={className}>{inline(block.content)}</p>
    }
    case 'hr':
      return <hr className="border-[color-mix(in_srgb,var(--line)_60%,transparent)]" />
    case 'images':
      return <AgentImageStrip toolName="display_images" images={block.images} />
    case 'table':
      return (
        <div className="max-w-full overflow-x-auto rounded-lg ring-1 ring-[color-mix(in_srgb,var(--line)_75%,transparent)]">
          <table className="w-full border-collapse text-[12px] leading-6">
            <thead>
              <tr className="bg-[color-mix(in_srgb,var(--surface-muted)_60%,var(--panel)_40%)]">
                {block.header.map((cell, index) => (
                  <th
                    key={index}
                    style={{ textAlign: block.aligns[index] ?? 'left' }}
                    className="border-b border-r border-[color-mix(in_srgb,var(--line)_65%,transparent)] px-2.5 py-1.5 font-semibold whitespace-nowrap last:border-r-0"
                  >
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  // 奇偶行斑马纹，颜色走主题变量保证深浅色主题下都可读
                  className={rowIndex % 2 === 1 ? 'bg-[color-mix(in_srgb,var(--surface-muted)_42%,transparent)]' : undefined}
                >
                  {block.header.map((_, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{ textAlign: block.aligns[cellIndex] ?? 'left' }}
                      className="border-b border-r border-[color-mix(in_srgb,var(--line)_50%,transparent)] px-2.5 py-1.5 align-top last:border-r-0"
                    >
                      {inline(row[cellIndex] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    default:
      return <p className="whitespace-pre-wrap">{inline(block.content)}</p>
  }
}

function inline(text: string): ReactNode {
  const nodes: ReactNode[] = []
  const re =
    /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|https?:\/\/[^\s<>")\]]+|(?<![\w(])\/uploads\/[^\s<>")\]]+)/g
  let last = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(text)) != null) {
    if (match.index > last) nodes.push(text.slice(last, match.index))
    const token = match[0]
    if (token.startsWith('`')) {
      nodes.push(
        <code key={key++} className="rounded bg-[color-mix(in_srgb,var(--surface-muted)_80%,transparent)] px-1 py-0.5 text-[12px] text-ink">
          {token.slice(1, -1)}
        </code>,
      )
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key++} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      )
    } else if (token.startsWith('*')) {
      nodes.push(
        <em key={key++} className="italic">
          {token.slice(1, -1)}
        </em>,
      )
    } else if (isImageLinkUrl(trimUrlTail(token))) {
      // 模型经常把图片直链当正文输出——渲染成「图片链接」文字超链接，点击新开页查看
      const url = trimUrlTail(token)
      nodes.push(
        <a
          key={key++}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-[var(--accent)] underline underline-offset-2 transition hover:opacity-80"
          title="点击查看图片"
        >
          图片链接
        </a>,
      )
      // 句尾标点（如「https://x.png。」）被正则吞进 token 时补回，避免标点丢失
      if (url.length < token.length) {
        nodes.push(token.slice(url.length))
      }
    } else {
      nodes.push(token)
    }
    last = match.index + token.length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

/** 去掉 URL 尾部被一并匹配到的句读标点（中文句号、逗号、括号等） */
function trimUrlTail(url: string): string {
  return url.replace(/[.,;:!?)。，、；：！？）】》"']+$/, '')
}

/** 是否为图片直链：扩展名判断，或站内 /uploads 资源 */
function isImageLinkUrl(url: string): boolean {
  if (!url) return false
  const path = url.split('?')[0]?.split('#')[0] ?? ''
  if (path.startsWith('/uploads/')) return true
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(path)
}
